import React, { useState, useEffect, useRef } from 'react';
import { Modal } from 'react-bootstrap';
import { useAuth0 } from '@auth0/auth0-react';
import { TodaySettings, UpdateTodaySettingsResponse } from '@dreamai/types';
import api from '../../api';
import { ReactComponent as PlusCircleIcon } from '../../icons/plus-circle.svg';
import './today-settings-modal.scss';

interface TodaySettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  settings: TodaySettings | null;
  onSaved: () => void;
}

const BROWSER_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

const QUICK_ADD_OPTIONS = [
  { label: 'News headlines', text: 'Start with the latest news headlines' },
  {
    label: 'Calendar summary',
    text: "Include a calendar summary of what I'm doing that day",
  },
  {
    label: 'Weather',
    text: 'Include an overview of the weather where you are',
  },
];

const TodaySettingsModal: React.FC<TodaySettingsModalProps> = ({
  isOpen,
  onClose,
  settings,
  onSaved,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const [requirements, setRequirements] = useState('');
  const [deliveryTime, setDeliveryTime] = useState('07:00');
  const [saving, setSaving] = useState(false);

  // Sync local state when settings change or modal opens
  useEffect(() => {
    if (isOpen) {
      setRequirements(settings?.requirements || '');
      setDeliveryTime(settings?.deliveryTime || '07:00');
    }
  }, [isOpen, settings]);

  const handleQuickAdd = (text: string) => {
    setRequirements((prev) => {
      const trimmed = prev.trim();
      if (trimmed.includes(text)) return prev;
      return trimmed ? `${trimmed}\n${text}` : text;
    });
    setTimeout(() => textareaRef.current?.focus(), 50);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = await getAccessTokenSilently();
      const res = await api.put<UpdateTodaySettingsResponse>(
        '/today/settings',
        {
          requirements: requirements || null,
          deliveryTime,
          deliveryTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          deliveryEmail: null,
          active: true,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.data.success) {
        onSaved();
        onClose();
      }
    } catch (err) {
      console.error('Error saving today settings:', err);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      show
      onHide={onClose}
      centered
      dialogClassName='today-settings-modal'
    >
      <Modal.Header closeButton>
        <Modal.Title>Customise your briefing</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <h1>What I'll include</h1>
        <div className='quick-add-buttons'>
          {QUICK_ADD_OPTIONS.map((opt) => (
            <button
              key={opt.label}
              className='quick-add-button'
              onClick={() => handleQuickAdd(opt.text)}
            >
              {opt.label}
              <PlusCircleIcon />
            </button>
          ))}
        </div>

        <textarea
          ref={textareaRef}
          className='requirements-textarea'
          placeholder='Tell your secretary what to include in your daily update…'
          value={requirements}
          onChange={(e) => setRequirements(e.target.value)}
          rows={5}
        />

        <div className='settings-fields'>
          <div className='field'>
            <label htmlFor='today-generation-time'>
              Your update gets generated at
            </label>
            <input
              id='today-generation-time'
              type='time'
              value={(() => {
                const [h, m] = deliveryTime.split(':').map(Number);
                const total = h * 60 + m - 10;
                const adjMin = ((total % 1440) + 1440) % 1440;
                const hh = String(Math.floor(adjMin / 60)).padStart(2, '0');
                const mm = String(adjMin % 60).padStart(2, '0');
                return `${hh}:${mm}`;
              })()}
              disabled
            />
            <span className='field-hint'>
              This is 10 minutes before your daily catch-up, which can be edited
              in settings.
            </span>
            {settings?.deliveryTimezone &&
              settings.deliveryTimezone !== BROWSER_TZ && (
                <span className='tz-hint'>* {settings.deliveryTimezone}</span>
              )}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button type='button' onClick={onClose}>
          Cancel
        </button>
        <button
          type='button'
          className='action-btn'
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default TodaySettingsModal;
